import {Alert, closeCurrentDialog, Form, FormField, showNotification} from 'platform/components';
import {VStack} from 'platform/foundation';
import {z} from 'zod';

import {defaultTo} from 'ramda';

import {usePatchCoreExchangeWarehouseReceiptMutation} from '@dms/api/warehouse';
import i18n from '@dms/i18n';
import {handleApiError} from '@dms/shared';

import {EMPTY_PLACEHOLDER, Nullish, RequiredTestIdProps, suffixTestId} from 'shared/web';

import {DialogActions} from './DialogActions';

const WarehouseReceiptFormSchema = z.object({
  warehouseName: z.string(),
  warehouseStorageNote: z.string(),
});

type WarehouseReceiptForm = z.infer<typeof WarehouseReceiptFormSchema>;

interface WarehouseReceiptDialogProps extends RequiredTestIdProps {
  coreExchangeId: string;
  warehouseName: string | Nullish;
  onSaved: VoidFunction;
}

export function WarehouseReceiptDialog(props: WarehouseReceiptDialogProps) {
  const [patchWarehouseReceipt, {isLoading}] = usePatchCoreExchangeWarehouseReceiptMutation();

  const handleSubmit = async (values: WarehouseReceiptForm) => {
    await patchWarehouseReceipt({
      coreExchangeId: props.coreExchangeId,
      body: {warehouseStorageNote: values.warehouseStorageNote},
    })
      .unwrap()
      .then(() => {
        showNotification.success(i18n.t('general.notifications.successfullyUpdated'));
        props.onSaved();
        closeCurrentDialog();
      })
      .catch(handleApiError);
  };

  return (
    <Form<WarehouseReceiptForm>
      experimentalZodSchema={WarehouseReceiptFormSchema}
      onSubmit={handleSubmit}
      defaultValues={{
        warehouseName: defaultTo(EMPTY_PLACEHOLDER, props.warehouseName),
        warehouseStorageNote: '',
      }}
    >
      {(control) => (
        <VStack spacing={4}>
          <Alert
            type="inline"
            variant="warning"
            message={i18n.t('entity.warehouse.labels.coreExchangeWarehouseReceiptWarning')}
            data-testid={suffixTestId('warning', props)}
          />
          {/* The core item is received to its own warehouse; the backend takes no warehouse. */}
          <FormField
            control={control}
            type="text"
            name="warehouseName"
            label={i18n.t('entity.warehouse.labels.warehouse')}
            isDisabled
            data-testid={suffixTestId('inputs.warehouse', props)}
          />
          <FormField
            control={control}
            type="textarea"
            name="warehouseStorageNote"
            label={i18n.t('entity.warehouse.labels.note')}
            data-testid={suffixTestId('inputs.warehouseStorageNote', props)}
          />
          <DialogActions
            control={control}
            isLoading={isLoading}
            data-testid={props['data-testid']}
          />
        </VStack>
      )}
    </Form>
  );
}
